import {
  EMPTY_WORKSPACE_AGENT_PILLS,
  parsePills,
  type WorkspaceAgentMessagePill,
} from '~/components/v2/components/workspace/workspace-agent-panel/schemas/pillMetadata';
import { type WorkspaceAgentMessage } from '~/components/v2/components/workspace/workspace-agent-panel/types/workspaceAgentMessage';
import { findWorkspaceAgentMessagePill } from '~/components/v2/components/workspace/workspace-agent-panel/utils/findWorkspaceAgentMessagePill';
import {
  readWorkspaceAgentArtifactHandles,
  type WorkspaceAgentArtifactListHandle,
  withWorkspaceAgentArtifactHandleCount,
} from '~/components/v2/components/workspace/workspace-agent-panel/utils/readWorkspaceAgentArtifactHandles';
import { synthesizeWorkspaceAgentPillsFromContent } from '~/components/v2/components/workspace/workspace-agent-panel/utils/synthesizeWorkspaceAgentPillsFromContent';
import {
  markWorkspaceAgentArtifactHydrationPill,
  workspaceAgentArtifactPillNeedsRowHydration,
} from '~/components/v2/components/workspace/workspace-agent-panel/utils/workspaceAgentArtifactHydration';
import { markWorkspaceAgentCommentMonikerPill } from '~/components/v2/components/workspace/workspace-agent-panel/utils/workspaceAgentCommentMoniker';
import { markWorkspaceAgentDirectAssetMonikerPill } from '~/components/v2/components/workspace/workspace-agent-panel/utils/workspaceAgentDirectAssetMoniker';

export type WorkspaceAgentMessagePillsById = ReadonlyMap<string, readonly WorkspaceAgentMessagePill[]>;

type WorkspaceAgentSynthesizedPills = {
  content: string;
  pills: readonly WorkspaceAgentMessagePill[];
};

type WorkspaceAgentMergedPills = {
  content: string;
  metadata: Record<string, unknown>;
  pills: readonly WorkspaceAgentMessagePill[];
};

export type WorkspaceAgentMessagePillsCache = {
  /** Latest merged pills for a message. A streamed token replaces the previous content. */
  merged: Map<string, WorkspaceAgentMergedPills>;
  parsed: WeakMap<Record<string, unknown>, readonly WorkspaceAgentMessagePill[]>;
  /** Latest synthesized pills for a message. A streamed token replaces the previous content. */
  synthesized: Map<string, WorkspaceAgentSynthesizedPills>;
};

export const createWorkspaceAgentMessagePillsCache = (): WorkspaceAgentMessagePillsCache => ({
  merged: new Map(),
  parsed: new WeakMap(),
  synthesized: new Map(),
});

export type BuildWorkspaceAgentMessagePillsByIdParams = {
  cache: WorkspaceAgentMessagePillsCache;
  messages: readonly WorkspaceAgentMessage[];
};

const getSynthesizedPills = (
  cache: WorkspaceAgentMessagePillsCache,
  messageId: string,
  content: string | null,
): readonly WorkspaceAgentMessagePill[] => {
  const key = content ?? '';
  const cached = cache.synthesized.get(messageId);

  if (cached?.content === key) {
    return cached.pills;
  }

  const synthesized = synthesizeWorkspaceAgentPillsFromContent(content);
  cache.synthesized.set(messageId, { content: key, pills: synthesized });

  return synthesized;
};

const getParsedPills = (
  cache: WorkspaceAgentMessagePillsCache,
  metadata: Record<string, unknown> | null,
): readonly WorkspaceAgentMessagePill[] => {
  if (!metadata) {
    return EMPTY_WORKSPACE_AGENT_PILLS;
  }

  const cached = cache.parsed.get(metadata);

  if (cached) {
    return cached;
  }

  const parsed = parsePills(metadata);
  cache.parsed.set(metadata, parsed);

  return parsed;
};

const getMergedPills = ({
  cache,
  content,
  extra,
  messageId,
  metadata,
  parsed,
}: {
  cache: WorkspaceAgentMessagePillsCache;
  content: string | null;
  extra: readonly WorkspaceAgentMessagePill[];
  messageId: string;
  metadata: Record<string, unknown>;
  parsed: readonly WorkspaceAgentMessagePill[];
}): readonly WorkspaceAgentMessagePill[] => {
  const contentKey = content ?? '';
  const cached = cache.merged.get(messageId);

  if (cached?.metadata === metadata && cached.content === contentKey) {
    return cached.pills;
  }

  const merged = [...parsed, ...extra];
  cache.merged.set(messageId, { content: contentKey, metadata, pills: merged });

  return merged;
};

const isVersionlessAssetPill = (
  pill: WorkspaceAgentMessagePill,
): pill is Extract<WorkspaceAgentMessagePill, { type: 'asset' }> =>
  pill.type === 'asset' && pill.versionId === undefined;

const isArtifactCitationPill = (pill: WorkspaceAgentMessagePill): boolean =>
  pill.type === 'assetList' || pill.type === 'boardList' || pill.type === 'changePreview' || pill.type === 'table';

const rememberSynthesizedPill = (
  candidate: WorkspaceAgentMessagePill,
  handles: ReadonlyMap<string, WorkspaceAgentArtifactListHandle>,
  parsed: readonly WorkspaceAgentMessagePill[],
  extra: WorkspaceAgentMessagePill[],
  replacements: Map<string, WorkspaceAgentMessagePill>,
): void => {
  const existing = findWorkspaceAgentMessagePill(parsed, candidate.id);
  const directAsset = isVersionlessAssetPill(candidate);
  const sized = withWorkspaceAgentArtifactHandleCount(candidate, handles);

  if (existing) {
    // A copy, not a field write: the parsed array is shared by identity.
    const raised =
      (existing.type === 'assetList' || existing.type === 'boardList') &&
      sized.type === existing.type &&
      sized.count > existing.count
        ? { ...existing, count: sized.count }
        : existing;

    if (raised !== existing) {
      replacements.set(existing.id, raised);
    }

    if (workspaceAgentArtifactPillNeedsRowHydration(raised)) {
      markWorkspaceAgentArtifactHydrationPill(raised);
    }

    if (directAsset && isVersionlessAssetPill(existing)) {
      markWorkspaceAgentDirectAssetMonikerPill(existing);
    }

    return;
  }

  if (isArtifactCitationPill(sized)) {
    markWorkspaceAgentArtifactHydrationPill(sized);
  }

  if (directAsset) {
    markWorkspaceAgentDirectAssetMonikerPill(sized);
  }

  if (sized.type === 'comment') {
    markWorkspaceAgentCommentMonikerPill(sized);
  }

  extra.push(sized);
};

const pillsForMessage = (
  cache: WorkspaceAgentMessagePillsCache,
  message: WorkspaceAgentMessage,
): readonly WorkspaceAgentMessagePill[] => {
  const parsed = getParsedPills(cache, message.metadata);
  const synthesized = getSynthesizedPills(cache, message.id, message.content);
  const handles = readWorkspaceAgentArtifactHandles(message.metadata);
  const extra: WorkspaceAgentMessagePill[] = [];
  const replacements = new Map<string, WorkspaceAgentMessagePill>();

  for (const candidate of synthesized) {
    rememberSynthesizedPill(candidate, handles, parsed, extra, replacements);
  }

  // A multi-select is an artifact handle, not a pill. The scope bar still needs a list pill, and
  // only on the user message that attached it — an assistant citation is already in `content`.
  if (message.role === 'user') {
    for (const handle of handles.values()) {
      if (findWorkspaceAgentMessagePill(parsed, handle.id) || extra.some((pill) => pill.id === handle.id)) {
        continue;
      }

      const pill: WorkspaceAgentMessagePill = {
        count: handle.count,
        id: handle.id,
        type: handle.type,
      };

      markWorkspaceAgentArtifactHydrationPill(pill);
      extra.push(pill);
    }
  }

  const projected = replacements.size === 0 ? parsed : parsed.map((pill) => replacements.get(pill.id) ?? pill);

  if (extra.length === 0 && replacements.size === 0) {
    return parsed.length > 0 ? parsed : EMPTY_WORKSPACE_AGENT_PILLS;
  }

  // Handle counts copy the synthesized placeholder. The cached synthesis still
  // has count 1 and is unmarked, so a sized copy has to be what the chip reads.
  const sizedCopies =
    !!message.metadata &&
    (extra.length !== synthesized.length || extra.some((pill, index) => pill !== synthesized[index]));

  if (!message.metadata || (!sizedCopies && parsed.length === 0)) {
    return synthesized;
  }

  return getMergedPills({
    cache,
    content: message.content,
    extra,
    messageId: message.id,
    metadata: message.metadata,
    parsed: projected,
  });
};

/**
 * Skill text is stored on tool and system rows (`@[assetVersion:<assetClipId>]`). Those rows are
 * not shown. Scanning them sends the example tokens to the clip, comment, and board lookups.
 */
const isWorkspaceAgentCitationMessage = (message: WorkspaceAgentMessage): boolean =>
  message.role === 'assistant' || message.role === 'user';

/** Reuses each message's parsed array while its metadata is the same object — identity is the point. */
export const buildWorkspaceAgentMessagePillsById = ({
  cache,
  messages,
}: BuildWorkspaceAgentMessagePillsByIdParams): WorkspaceAgentMessagePillsById => {
  const byMessageId = new Map<string, readonly WorkspaceAgentMessagePill[]>();

  for (const message of messages) {
    byMessageId.set(
      message.id,
      isWorkspaceAgentCitationMessage(message) ? pillsForMessage(cache, message) : EMPTY_WORKSPACE_AGENT_PILLS,
    );
  }

  return byMessageId;
};
